import { FC } from 'react';
import { FormProvider, get } from 'react-hook-form';
import styled from 'styled-components';
import { Col, Row } from 'react-bootstrap';
import { PopUp } from '../../../common/components/PopUp';
import { Colors } from '../../../assets';
import { FontSize, Spacing } from '../../../assets/GlobalStyle/Variables';
import { useSidebarContext } from '../../../common/components/Sidebar/SidebarContext';

import { useLabMaterials } from './useLabMaterials';
import { LabMaterialsFormSkeleton } from './LabMaterialsFormSkeleton';
import {
  ControlSelect,
  Field,
  Textarea
} from '../../../common/components/Form';
import PulseLoader from 'react-spinners/PulseLoader';

type LabMaterialsFormProps = {
  protocolId?: number;
};

type MaterialItem = {
  key: string;
  label: string;
  type?: string;
};

const MATERIAL_FLUIDS: MaterialItem[] = [
  { key: 'femoral_blood_tubes', label: 'krev femorální', type: 'tubes' },
  { key: 'venous_sinus_blood_tubes', label: 'krev splavy', type: 'tubes' },
  { key: 'cardiac_cavity_blood_tubes', label: 'krev ze srdečních dutin', type: 'tubes' },
  { key: 'subclavian_blood_tubes', label: 'krev podklíčková', type: 'tubes' },
  { key: 'bladder_lavage_ml', label: 'výplach z močového měchýře', type: 'ml' },
];

const MATERIAL_ITEMS: MaterialItem[] = [
  { key: 'organs', label: 'orgány' },
  { key: 'vitreous_humor', label: 'sklivec' },
  { key: 'gastric_content', label: 'žaludeční obsah' },
  { key: 'hair', label: 'vlasy' },
  { key: 'meconium', label: 'smolka' },
  { key: 'urine', label: 'moč' }
];

const MATERIAL_OTHERS: MaterialItem[] = [
  { key: 'other_text', label: 'jiné', type: 'textarea' },
  { key: 'other_blood_text', label: 'krev - jiná', type: 'textarea' }
];

const checkboxFieldSuffix = '_on';

export const LabMaterialsForm: FC<LabMaterialsFormProps> = (props): JSX.Element => {
  const { protocolId } = props;
  const {
    fields,
    methods,
    message,
    loading,
    printing,
    options,
    togglePopup,
    formatISODatetime,
    handleHandoverProtocolPreview,
    handleHandoverProtocolPrint,
    handleReset,
    onSubmit
  } = useLabMaterials({ protocolId });

  const { setValue, watch } = methods;
  const { isCollapsed } = useSidebarContext();

  if (loading) {
    return <LabMaterialsFormSkeleton />;
  }

  const registerField = (fields: Record<string, any>, key: string): any => {
    const field = key.split('.').reduce((obj, k) => (obj ? obj[k] : undefined), fields);
    return methods.register(field.name, { required: field.required });
  }

  const registerCheckboxForField = (fields: Record<string, any>, key: string): any => {
    return methods.register(fields[`${key}${checkboxFieldSuffix}`].name);
  }

  return (loading) ? 
    <LabMaterialsFormSkeleton /> : (
    <>
    {message && <PopUp message={message.message} toggle={togglePopup} type={message.type} />}
      <FormProvider {...methods}>
        <StyledForm onSubmit={methods.handleSubmit(onSubmit)}>
          {/*<Row>
            <BasePartCol xl={3} lg={12} md={12}>
              Materiál odebraný k vyšetření:
            </BasePartCol>
            <LeftPartCol xl={5} lg={12} md={12}>
              {MATERIAL_FLUIDS.map((i) => {
                const value = watch(i.key as any);
                const checked = watch(`${i.key}${checkboxFieldSuffix}` as any);
                return (
                  <MaterialRow key={i.key}>
                    <CheckItem>
                      <input
                        type="checkbox"
                        {...registerCheckboxForField(fields, i.key)}
                        checked={!!checked}
                        onChange={e => {
                          setValue(`${i.key}${checkboxFieldSuffix}` as any, e.target.checked);
                          if (e.target.checked && (!value || value === 0)) {
                            setValue(i.key as any, 1);
                          } else if (!e.target.checked) {
                            setValue(i.key as any, i.type === 'text' ? '' : 0);
                          }
                        }}
                      />
                      <span>{i.label}</span>
                    </CheckItem>
                    {i.type === 'tubes' && (
                      <MaterialInline>
                        <SmallLabel>počet zkumavek:</SmallLabel>
                        <SmallInput
                          type="number"
                          min="0"
                          {...registerField(fields, i.key)}
                          value={value ?? ''}
                          onChange={e => {
                            const val = Number(e.target.value);
                            setValue(i.key as any, val);
                            setValue(`${i.key}${checkboxFieldSuffix}` as any, val > 0);
                          }}
                        />
                      </MaterialInline>
                    )}
                    {i.type === 'ml' && (
                      <MaterialInline>
                        <SmallInput
                          type="number"
                          min="0"
                          step="0.1"
                          {...registerField(fields, i.key)}
                          value={value ?? ''}
                          onChange={e => {
                            const val = Number(e.target.value);
                            setValue(i.key as any, val);
                            setValue(`${i.key}${checkboxFieldSuffix}` as any, val > 0);
                          }}
                        />
                        <SmallLabelSuffix>{i.type}</SmallLabelSuffix>
                      </MaterialInline>
                    )}
                  </MaterialRow>
                );
              })}
            </LeftPartCol>

            <RightPartCol xl={3} lg={12} md={12} className='right-part'>
              {MATERIAL_ITEMS.map((i) => (
                <MaterialRow key={i.key}>
                  <CheckItem>
                    <input type="checkbox" {...registerField(fields, i.key)} />
                    <span>{i.label}</span>
                  </CheckItem>
                </MaterialRow>
              ))}
            </RightPartCol>
          </Row>

          <Row>
            <Col xl={3} lg={12} md={12}></Col>
            <Col xl={8} lg={12} md={12}>
              {MATERIAL_OTHERS.map((i) => {
                const value = watch(i.key as any);
                return (
                  <MaterialTextareaRow key={i.key}>
                    <CheckItem>
                      <input type="checkbox" {...registerCheckboxForField(fields, i.key)} />
                      <span>{i.label}</span>
                    </CheckItem>

                    <MaterialInline>
                      <StyledTextarea
                        {...registerField(fields, i.key)}
                        value={value ?? ''}
                        onChange={e => {
                          const val = e.target.value;
                          setValue(i.key as any, val);
                          setValue(`${i.key}${checkboxFieldSuffix}` as any, val.length > 0);
                        }}
                        placeholder="Vyplňte"
                      />
                    </MaterialInline>
                  </MaterialTextareaRow>
                );
              })}
            </Col>
          </Row>
          <SectionDivider />*/}

          <>
              <HandoverProtocolRow>
                <Col xl={11} lg={12} md={12}>
                  <Textarea {...fields.handover_protocol.material_description} label='Druh a množství přebíraného materiálu' rows={4}/>
                </Col>
              </HandoverProtocolRow>

              <HandoverProtocolRow>
                <Col xl={6} lg={12} md={12}>
                  <Field {...fields.handover_protocol.handover_date} label='Datum předání a čas předání' />
                </Col>
                <Col xl={2} lg={12} md={12}>
                  <Field {...fields.handover_protocol.handover_time} />
                </Col>
                <Col xl={1} lg={12} md={12}>
                  <HandoverActionButtons>
                    <StyledOutlineButton type="button" onClick={handleHandoverProtocolPreview}>
                      Náhled
                    </StyledOutlineButton>
                    <StyledOutlineButton type="button" onClick={handleHandoverProtocolPrint}>
                      {!printing ? 'Tisk' : <PulseLoader className="pulse-loader" size={6} color="#3c1149"/>}
                    </StyledOutlineButton>
                  </HandoverActionButtons>
                </Col>
              </HandoverProtocolRow>

              <HandoverProtocolRow>
                <Col xl={6} lg={12} md={12}>
                  <ControlSelect
                      {...fields.handover_protocol.handed_over_by}
                      label='Předávající'
                      options={options.employees}
                      autoTransform={true}
                    />
                </Col>
              </HandoverProtocolRow>

              <HandoverProtocolRow>
                <Col xl={6} lg={12} md={12}>
                  <ControlSelect
                      {...fields.handover_protocol.received_by}
                      label='Přebírající'
                      options={options.employees}
                      autoTransform={true}
                    />
                </Col>
              </HandoverProtocolRow>
          </>

          <Row>
            <Col md={12}>
              <StyledActionButtonsWrapper $isCollapsed={isCollapsed}>
                <StyledButtonsWrapperLeft>
              
                </StyledButtonsWrapperLeft>
                <StyledButtonsWrapperCenter>
                  <StyledOutlineButton type="button" onClick={handleReset}>
                    Vymazat údaje
                  </StyledOutlineButton>
                </StyledButtonsWrapperCenter>
                <StyledButtonsWrapperRight>
                  <StyledPrimaryButton type="submit">
                    Uložit
                  </StyledPrimaryButton>
                </StyledButtonsWrapperRight>
              </StyledActionButtonsWrapper>
            </Col>
          </Row>

        </StyledForm>
      </FormProvider>
    </>
  );
  
}

const StyledForm = styled.form`
  padding-top: 1.5rem;
  padding-bottom: 100px;
  padding-left: 1rem;

  input[type='checkbox'] {
    cursor: pointer;
  }

  span[role='alert'] {
    display: block;
    margin: 2px 0 5px 0;
    color: ${Colors.error};
    font-size: ${FontSize.small};
  }
`;

const MaterialRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: ${Spacing(2)};
  align-items: center;
  margin-bottom: ${Spacing(2)};

  @media (max-width: 700px) {
    display: block;
    margin-bottom: ${Spacing(4)};
  }
`;

const MaterialTextareaRow = styled.div`
  display: grid;
  grid-template-columns: .25fr 1fr;
  gap: ${Spacing(4)};
  align-items: baseline;
  margin: ${Spacing(4)} 0 ${Spacing(6)} 0;

  @media (max-width: 700px) {
    display: block;
  }
`;

const HandoverProcessedRow = styled(Row)`
  margin-bottom: ${Spacing(8)};
`;

const HandoverProtocolRow = styled(Row)`
  margin-top: ${Spacing(3)};
  
  label {
    min-width: 25rem;
  }
`;

const HandoverCreatedInfo = styled.span`
  color: ${Colors.grey};
  font-size: ${FontSize.medium};
`;

const HandoverActionButtons = styled.div`
  display: flex;
  gap: ${Spacing(2)};
`;

const MaterialInline = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing(2)};
`;

const CheckItem = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  user-select: none;
  cursor: pointer;

  input[type='checkbox'] {
    width: 18px;
    height: 18px;
  }

  span {
    color: ${Colors.grey};
  }
`;

const StyledTextarea = styled.textarea<{ $hasError?: boolean }>`
  width: 100%;
  min-height: 60px;
  border: 1px solid ${(props) => (props.$hasError ? Colors.error : Colors.disabledHover)};
  border-radius: 4px;
  padding: ${Spacing(3)} ${Spacing(4)};
  font-size: 15px;
  line-height: 25px;
  resize: vertical;
`;

const SmallLabel = styled.div`
  color: ${Colors.grey};
  font-size: 15px;
  white-space: nowrap;
`;

const SmallLabelSuffix = styled.div`
  position: absolute;
  right: -2px;
  color: ${Colors.grey};
  font-size: 15px;
  white-space: nowrap;

  @media (max-width: 700px) {
    position: relative;
    right: 0;
  }
`;

const SmallInput = styled.input`
  width: 70px;
  height: 32px;
  border: 1px solid ${Colors.lightGrey};
  padding: 0 ${Spacing(2)};
`;

const BasePartCol = styled(Col)`
  marginBottom: '2rem'
`;

const LeftPartCol = styled(Col)`
  padding-left: 1rem;
  padding-right: 5rem;

  @media (max-width: 700px) {
    padding-right: 1rem;
  }

  ${SmallLabelSuffix} {
    right: calc(4rem - 4px);

    @media (max-width: 700px) {
      right: 0;
    }
  }
`;

const RightPartCol = styled(Col)`
 
`;

const SectionDivider = styled.hr`
  border: none;
  border-top: 1px solid ${Colors.lightGrey};
  margin: ${Spacing(4)} 0;
`;

const StyledActionButtonsWrapper = styled.div<{ $isCollapsed: boolean }>`
  display: flex;
  gap: 1rem;
  justify-content: space-between;
  width: auto;
  position: fixed;
  bottom: 0;
  left: ${(props) => (props.$isCollapsed ? '59px' : '178px')};
  right: 0;
  background-color: #ffffffec;
  padding: 1rem;
  border-top: 2px solid ${Colors.lightGrey};
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
  z-index: 1;
  transition: left 0.3s ease;
`;

const StyledButtonsWrapperLeft = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-start;
  flex: 1;
`;

const StyledButtonsWrapperCenter = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex: 1;
`;

const StyledButtonsWrapperRight = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  flex: 1;
`;

const StyledPrimaryButton = styled.button`
  background-color: ${Colors.primary};
  color: #fff;
  border: 1px solid ${Colors.primary};
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  border-radius: 4px;

  &:hover {
    background-color: ${Colors.secondary};
  }
`;

const StyledOutlineButton = styled(StyledPrimaryButton)`
  display: flex;
  background-color: #fff;
  color: ${Colors.primary};
  border: 1px solid ${Colors.primary};
  width: max-content;

  &:hover {
    background-color: ${Colors.disabledHover};
  }
`;